import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  TrackByFunction,
} from '@angular/core';
import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';
import isString from 'lodash/isString';
import partition from 'lodash/partition';
import sortBy from 'lodash/sortBy';
import { combineLatest, Subject, Subscription } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

import { getInitials } from '../../string.utils';
import { ProfileService } from '../../../profile/profile.service';
import { stringToHexColor } from '../../color.utils';
import { User } from '../../models/user.model';

export interface AvatarUserInput extends User {
  access?: string
}

interface AvatarUser extends AvatarUserInput {
  avatarColor: string
  info: string
  initials: string
  hasValidImage: boolean
}

@Component({
  host: { 'test-id': 'app-avatar' },
  selector: 'app-avatar',
  styleUrls: ['./app-avatar.component.scss'],
  templateUrl: './app-avatar.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppAvatarComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() enableAnimation = false
  @Input() enablePopover = true
  @Input() hideCurrentUser = false
  @Input() users: AvatarUserInput[] | AvatarUserInput

  public avatarMax = 10
  public extraUsers: AvatarUser[] = []
  public userList: AvatarUser[] = []

  private users$ = new Subject<AvatarUserInput[]>()
  private subscriptions = {
    users: Subscription.EMPTY,
  }

  constructor(
    private cdr: ChangeDetectorRef,
    private elementRef: ElementRef,
    private profileService: ProfileService,
  ) {
    this.subscriptions.users = combineLatest([
      this.users$.pipe(distinctUntilChanged((x, y) => isEqual(x, y))),
      this.profileService.getProfile(),
    ]).subscribe(([users, profile]) => {
      const sortedUsers = sortBy(users, 'lastname');
      const [adminUsers, nonAdminUsers] = partition(sortedUsers, user => /(^| )admin(,|$)/.test(user.access));
      this.userList = [...adminUsers, ...nonAdminUsers].map(user => {
        const avatar = isString(user.avatar) && !user.avatar.startsWith('https') ? '' : user.avatar;
        return {
          ...user,
          avatar,
          avatarColor: '#' + stringToHexColor(user._id),
          hasValidImage: Boolean(avatar),
          info: `${user.firstname} ${user.lastname}` + (user.access ? ` (${user.access})` : ''),
          initials: getInitials(user.firstname + ' ' + user.lastname),
        };
      });
      if (this.hideCurrentUser && profile) {
        this.userList = this.userList.filter(user => user._id !== profile._id);
      }
      this.cdr.markForCheck();
      this.calculateDisplay();
    });
  }

  ngAfterViewInit(): void {
    this.calculateDisplay();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.users && this.users
      && (changes.users.firstChange || this.users !== changes.users.previousValue)) {
      this.users$.next(Array.isArray(this.users) ? this.users : [this.users]);
    }
  }

  ngOnDestroy(): void {
    for (const subscription of Object.values(this.subscriptions)) {
      subscription.unsubscribe();
    }
  }

  getUserId: TrackByFunction<AvatarUser> = (index, user) => user._id

  /**
   * Calculate how many avatars + overflow icon can fit in the available space,
   * and move additional users to a list with a tooltip.
   */
  @HostListener('window:resize', ['$event'])
  calculateDisplay = debounce(() => {
    if (this.userList.length <= 1) {
      return;
    }
    const container = this.elementRef.nativeElement.closest('td, mat-cell');
    if (container) {
      this.avatarMax = Math.max(1, Math.floor((container.offsetWidth - 30) / 30 ) - 1);
    }
    this.extraUsers = this.userList.slice(this.avatarMax, 25);
    this.cdr.markForCheck();
  }, 100)

  onAvatarImageError(user: AvatarUser): void {
    user.hasValidImage = false;
    this.cdr.markForCheck();
  }
}
